import React, { useEffect } from 'react';
import {
    Box,
    FormControl,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { AboutInvestorRelationsOverviewMonthlyStatementsContent } from '../../graphql/pimcore/modules/siteContent/types/AboutInvestorRelationsContent';
import { FormattedMessage } from 'react-intl';

const StyledSelect = styled(Select)(() => ({
    borderRadius: '8px', // For rounded corners
    backgroundColor: '#fff', // White background
    padding: 0, // Adjust padding to match spacing
    height: '40px', // Adjust height to match the design
    fontSize: '16px',
    fontFamily: 'Poppins',
    '& .MuiSelect-icon': {
        color: '#4A4A4A', // Icon color
        fontSize: '1rem', // Adjust icon size
        right: '10px', // Move icon to the right
    },
    '& fieldset': {
        borderColor: 'transparent', // Remove default border
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#F2F2F2',
    },
}));

interface MonthlyStateTableProps {
    cmsContent: AboutInvestorRelationsOverviewMonthlyStatementsContent;
}

const MonthlyStateTable = ({ cmsContent }: MonthlyStateTableProps) => {
    const { isScreenLg, isScreenSm, isScreenXs } = useResponsive();

    // Table partitions from Pimcore
    const partitions = cmsContent.table.tablePartitions;

    // Initialize selected year to first available partition key
    const [selectedYear, setSelectedYear] = React.useState(partitions[0]?.selectionKey || '');

    // Ensure selectedYear is always valid, e.g. if cmsContent changes duriing loading
    useEffect(() => {
        if (!partitions.some((part) => part.selectionKey === selectedYear)) {
            setSelectedYear(partitions[0]?.selectionKey || '');
        }
    }, [partitions, selectedYear]);

    // Handle dropdown year change
    const handleYearChange = (event: SelectChangeEvent<string>) => {
        setSelectedYear(event.target.value);
    };

    // Dropdown options for each year partition
    const yearOptions = partitions.map((part) => (
        <MenuItem key={part.selectionKey} value={part.selectionKey} sx={{ fontFamily: 'Poppins' }}>
            <FormattedMessage
                id="Year"
                values={{ year: part.selectionKey }}
                defaultMessage="Year {year}"
            />
        </MenuItem>
    ));

    // Find the partition matching the selected year
    const currentPartition = partitions.find((part) => part.selectionKey === selectedYear);

    // Common table header (required, fallback if no partition-specific header)
    const commonHeader: string[] = cmsContent.table.commonTableHeader;
    // Partition-specific headers
    const partitionHeaders: string[] = currentPartition?.tableHeader ?? [];

    // Determine table headers: partition-specific header takes precedence over common header
    const headers: string[] = partitionHeaders.length > 0 ? partitionHeaders : commonHeader;

    const StyledHeaderCell = styled(TableCell)(() => ({
        backgroundColor: '#B95B3B',
        color: 'white',
        fontWeight: 500,
        fontSize: '12px',
        textAlign: 'center',
        fontFamily: 'Poppins',
        padding: isScreenLg ? '4px' : '8px',
    }));

    const cellSx = {
        fontWeight: 500,
        fontSize: '12px',
        textAlign: 'center',
        fontFamily: 'Poppins',
        padding: isScreenLg ? '6px' : '8px',
    };

    return (
        <>
            {/* Year selector */}
            <Box marginBottom="1rem" marginTop="0.5rem">
                <FormControl>
                    <StyledSelect
                        value={selectedYear}
                        onChange={
                            handleYearChange as (
                                event: SelectChangeEvent<unknown>,
                                child: React.ReactNode
                            ) => void
                        }
                        IconComponent={ArrowDropDownIcon}
                        MenuProps={{
                            PaperProps: {
                                sx: { fontFamily: 'Poppins' },
                            },
                        }}
                    >
                        {yearOptions}
                    </StyledSelect>
                </FormControl>
            </Box>

            {/* Table */}
            <Box marginBottom="0.2rem">
                <TableContainer
                    component={Paper}
                    sx={{
                        width: isScreenLg ? '530px' : '100%',
                        margin: isScreenLg ? '0' : '0 auto',
                        borderRadius: '0px',
                        overflow: isScreenSm || isScreenXs ? 'auto' : 'hidden',
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                {headers.map((header, idx) => (
                                    <StyledHeaderCell key={idx}>
                                        {header.split('\n').map((line, index, arr) => (
                                            <React.Fragment key={index}>
                                                {line}
                                                {index < arr.length - 1 && <br />}
                                            </React.Fragment>
                                        ))}
                                    </StyledHeaderCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentPartition?.tableBody.map((row, rowIdx) => (
                                <StyledTableRow key={rowIdx}>
                                    {row.map((cell, cellIdx) => (
                                        <TableCell key={cellIdx} sx={cellSx}>
                                            {cell}
                                        </TableCell>
                                    ))}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default MonthlyStateTable;
